import React from "react";
import { useTranslation } from "react-i18next";
import { Stack, Checkbox, TextField, Typography, Box } from "@mui/material";

export interface Block {
  weekday: number; // 0=Sun..6=Sat
  start: string; // "HH:MM"
  end: string;
}

// Mon..Sun display order.
const ORDER = [1, 2, 3, 4, 5, 6, 0];

/** One time range per weekday (the model supports more; this editor keeps it
 *  simple). Toggle a day on/off and set its start/end. */
export default function AvailabilityEditor({ blocks, onChange }: { blocks: Block[]; onChange: (b: Block[]) => void }) {
  const { i18n } = useTranslation();
  const wdName = (wd: number) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: "long" }).format(new Date(Date.UTC(2024, 0, 7 + wd)));

  const get = (wd: number) => blocks.find((b) => b.weekday === wd);
  const setDay = (wd: number, enabled: boolean, start: string, end: string) => {
    const others = blocks.filter((b) => b.weekday !== wd);
    onChange(enabled ? [...others, { weekday: wd, start, end }] : others);
  };

  return (
    <Stack spacing={1}>
      {ORDER.map((wd) => {
        const b = get(wd);
        const enabled = !!b;
        const start = b?.start || "09:00";
        const end = b?.end || "17:00";
        return (
          <Stack key={wd} direction="row" alignItems="center" spacing={1.5}>
            <Checkbox checked={enabled} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDay(wd, e.target.checked, start, end)} />
            <Typography sx={{ width: 110, textTransform: "capitalize", color: enabled ? "text.primary" : "text.secondary" }}>
              {wdName(wd)}
            </Typography>
            <TextField type="time" size="small" value={start} disabled={!enabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDay(wd, true, e.target.value, end)} sx={{ width: 120 }} />
            <Box component="span" sx={{ color: "text.secondary" }}>—</Box>
            <TextField type="time" size="small" value={end} disabled={!enabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDay(wd, true, start, e.target.value)} sx={{ width: 120 }} />
          </Stack>
        );
      })}
    </Stack>
  );
}
